import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchLatestBroadcastsAPI, refreshDashboardStatsAPI } from '../services/serverService';
import { db } from '../firebase';
import { collectionGroup, collection, query, orderBy, limit, onSnapshot, doc } from 'firebase/firestore';
import { hasPermission } from '../config/permissions';

export default function GlobalDashboardPage() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const { currentUser, userRole } = useAuth();
    const canCreateServer = hasPermission(userRole, 'CAN_MANAGE_GLOBAL_ADMINS');
    const canViewOtps = hasPermission(userRole, 'CAN_VIEW_SENSITIVE_OTPS');
    
    const [metrics, setMetrics] = useState({ totalServers: 0, totalStaff: 0, totalServiceUsers: 0 });
    const [serverRisk, setServerRisk] = useState({ nearingExpiry: 0, highCapacity: 0 });
    const [growth, setGrowth] = useState({ day: 0, week: 0, month: 0, yearToDate: 0 });
    
    const [activeAlerts, setActiveAlerts] = useState([]);
    const [pendingOtps, setPendingOtps] = useState([]);
    const [recentBroadcasts, setRecentBroadcasts] = useState([]);

    const parseDate = (value) => {
        if (!value) return null;
        if (value._seconds) return new Date(value._seconds * 1000);
        if (value.toDate) return value.toDate();
        if (typeof value === 'string') return new Date(value);
        const d = new Date(value);
        return isNaN(d) ? null : d;
    };

    const formatTime = (timeObj) => {
        const d = parseDate(timeObj);
        return d ? d.toLocaleString() : 'Unknown Time';
    };

    useEffect(() => {
        if (!currentUser) return;

        let unsubAlerts = () => {};
        let unsubOtps = () => {};
        let unsubMetrics = () => {};

        const loadDashboardData = async () => {
            try {
                // 1. Listen to Aggregated Dashboard Stats (Growth, Capacity, Counts)
                const metricsDocRef = doc(db, 'metrics', 'dashboardStats');
                unsubMetrics = onSnapshot(metricsDocRef, (docSnap) => {
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setMetrics({
                            totalServers: data.totalServers || 0,
                            totalStaff: data.totalStaff || 0,
                            totalServiceUsers: data.totalServiceUsers || 0
                        });
                        setServerRisk({
                            nearingExpiry: data.nearingExpiry || 0,
                            highCapacity: data.highCapacity || 0
                        });
                        setGrowth({
                            day: data.growthDay || 0,
                            week: data.growthWeek || 0,
                            month: data.growthMonth || 0,
                            yearToDate: data.growthYTD || 0
                        });
                    }
                    setLoading(false);
                }, (error) => {
                    console.error("Error listening to dashboard stats:", error);
                    setLoading(false);
                });

                // 2. Listen to Latest 5 Emergency Alerts
                try {
                    const alertsQuery = query(collectionGroup(db, 'emergencyAlerts'), orderBy('time', 'desc'), limit(5));
                    unsubAlerts = onSnapshot(alertsQuery, (snapshot) => {
                        setActiveAlerts(snapshot.docs.map(doc => ({ id: doc.id, serverId: doc.ref.parent.parent?.id, ...doc.data() })));
                    }, (error) => {
                        console.error("Alerts listener error (missing index?):", error);
                    });
                } catch (e) { console.error("Alerts query setup failed:", e); }

                // 3. Listen to Latest 5 OTPs
                try {
                    const otpQuery = query(collection(db, 'requested_otps'), orderBy('requested_at', 'desc'), limit(5));
                    unsubOtps = onSnapshot(otpQuery, (snapshot) => {
                        setPendingOtps(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                    }, (error) => {
                         console.error("OTP listener error:", error);
                    });
                } catch (e) { console.error("OTP query setup failed:", e); }

                // 4. Fetch Latest Broadcasts
                try {
                    const bResponse = await fetchLatestBroadcastsAPI();
                    if (bResponse.data?.success) {
                        setRecentBroadcasts(bResponse.data.data.slice(0, 5));
                    }
                } catch (e) { console.error("Broadcasts API failed:", e); }

            } catch (error) {
                console.error("Dashboard Load Error:", error);
                setLoading(false);
            }
        };

        loadDashboardData();

        return () => {
            unsubMetrics();
            unsubAlerts();
            unsubOtps();
        };
    }, [currentUser]);

    const handleRefreshStats = async () => {
        try {
            setRefreshing(true);
            await refreshDashboardStatsAPI();
        } catch (err) {
            console.error("Failed to refresh stats:", err);
        } finally {
            setRefreshing(false);
        }
    };

    if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>Loading Command Center...</div>;

    return (
        <div className="page-container" style={{ maxWidth: '1400px', margin: '0 auto', paddingBottom: '40px' }}>
            <div className="page-header" style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h1 style={{ color: '#2d3748', margin: '0 0 5px 0' }}>Global Command Center</h1>
                    <p style={{ color: '#718096', margin: 0 }}>Platform-wide overview, risks, and escalations.</p>
                </div>
                <button
                    onClick={handleRefreshStats}
                    disabled={refreshing}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 18px',
                        backgroundColor: refreshing ? '#edf2f7' : '#ffffff',
                        color: refreshing ? '#a0aec0' : '#2b6cb0',
                        border: '1px solid #cbd5e0',
                        borderRadius: '8px',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: refreshing ? 'not-allowed' : 'pointer',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                >
                    <span>🔄</span>
                    {refreshing ? 'Recalculating...' : 'Refresh Metrics'}
                </button>
            </div>

            {/* HERO CARDS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '20px' }}>
                <div onClick={() => navigate('/servers')} style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', borderTop: '4px solid #2b6cb0', cursor: 'pointer' }}>
                    <div style={{ color: '#718096', fontSize: '13px', fontWeight: 'bold', textTransform: 'uppercase' }}>Active Franchises / Servers</div>
                    <div style={{ fontSize: '36px', fontWeight: '800', color: '#2d3748', marginTop: '10px' }}>{metrics.totalServers}</div>
                </div>
                <div onClick={() => navigate('/global-directory', { state: { tab: 'staff' } })} style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', borderTop: '4px solid #38a169', cursor: 'pointer' }}>
                    <div style={{ color: '#718096', fontSize: '13px', fontWeight: 'bold', textTransform: 'uppercase' }}>Global Workforce (Admins + Emp)</div>
                    <div style={{ fontSize: '36px', fontWeight: '800', color: '#2d3748', marginTop: '10px' }}>{metrics.totalStaff.toLocaleString()}</div>
                </div>
                <div onClick={() => navigate('/global-directory', { state: { tab: 'users' } })} style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', borderTop: '4px solid #805ad5', cursor: 'pointer' }}>
                    <div style={{ color: '#718096', fontSize: '13px', fontWeight: 'bold', textTransform: 'uppercase' }}>Global Service Users</div>
                    <div style={{ fontSize: '36px', fontWeight: '800', color: '#2d3748', marginTop: '10px' }}>{metrics.totalServiceUsers.toLocaleString()}</div>
                </div>
            </div>

            {/* ACTION CENTER */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '20px' }}>
                
                {/* 1. Alerts */}
                <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ padding: '15px 20px', backgroundColor: '#fff5f5', borderBottom: '1px solid #fed7d7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, color: '#c53030', fontSize: '16px' }}>🚨 Latest 5 Emergency Alerts</h3>
                        <span onClick={() => navigate('/latest-alerts')} style={{ fontSize: '12px', color: '#c53030', cursor: 'pointer', fontWeight: 'bold' }}>View All →</span>
                    </div>
                    <div>
                        {activeAlerts.length === 0 ? <div style={{ padding: '20px', textAlign: 'center', color: '#718096' }}>No alerts found.</div> : 
                            activeAlerts.map(alert => (
                                <div key={`${alert.serverId || ''}_${alert.id}`} style={{ padding: '15px 20px', borderBottom: '1px solid #edf2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <div style={{ fontWeight: 'bold', color: '#2d3748' }}>{alert.username || 'Unknown User'}</div>
                                        <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>Type: {alert.type || 'N/A'}</div>
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#a0aec0', textAlign: 'right' }}>
                                        {formatTime(alert.time)}
                                        <div style={{ color: '#c53030', marginTop: '4px', fontWeight: 'bold' }}>{alert.serverId}</div>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </div>

                {/* 2. Broadcasts */}
                <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ padding: '15px 20px', backgroundColor: '#ebf8ff', borderBottom: '1px solid #bee3f8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, color: '#2b6cb0', fontSize: '16px' }}>📢 Latest 5 Broadcasts</h3>
                        <span onClick={() => navigate('/latest-broadcasts')} style={{ fontSize: '12px', color: '#2b6cb0', cursor: 'pointer', fontWeight: 'bold' }}>View All →</span>
                    </div>
                    <div>
                        {recentBroadcasts.length === 0 ? <div style={{ padding: '20px', textAlign: 'center', color: '#718096' }}>No broadcasts found.</div> : 
                            recentBroadcasts.map(b => (
                                <div key={`${b.id}`} style={{ padding: '15px 20px', borderBottom: '1px solid #edf2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <div style={{ fontWeight: 'bold', color: '#2d3748', fontSize: '13px' }}>{b.message}</div>
                                        <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>By: {b.createdBy}</div>
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#a0aec0', textAlign: 'right', whiteSpace: 'nowrap', marginLeft: '10px' }}>
                                        {formatTime(b.sendAt)}
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </div>

                {/* 3. OTPs */}
                <div style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ padding: '15px 20px', backgroundColor: '#fffaf0', borderBottom: '1px solid #feebc8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, color: '#dd6b20', fontSize: '16px' }}>🔐 Latest 5 Login OTPs</h3>
                        
                        {/* Hide the 'View All' link from Support */}
                        {canViewOtps && (
                            <span onClick={() => navigate('/requested-otps')} style={{ fontSize: '12px', color: '#dd6b20', cursor: 'pointer', fontWeight: 'bold' }}>View All →</span>
                        )}
                    </div>
                    <div>
                        {/* If they don't have permission OR the list is empty, show the blank state */}
                        {(!canViewOtps || pendingOtps.length === 0) ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#718096' }}>No security requests.</div> 
                        ) : (
                            pendingOtps.map(otp => (
                                <div key={otp.id} style={{ padding: '15px 20px', borderBottom: '1px solid #edf2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <div style={{ fontWeight: 'bold', color: '#2d3748', fontSize: '13px' }}>{otp.email || 'Unknown'}</div>
                                        <div style={{ fontSize: '12px', color: '#a0aec0', marginTop: '4px' }}>{formatTime(otp.requested_at)}</div>
                                    </div>
                                    <span style={{ backgroundColor: '#e3f2fd', color: '#0d47a1', padding: '4px 8px', borderRadius: '4px', fontFamily: 'monospace', fontWeight: 'bold', letterSpacing: '1px' }}>
                                        {otp.otp || 'N/A'}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

            </div>

            {/* ROW 3: Operations & Health */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                    <h3 style={{ margin: '0 0 20px 0', color: '#2d3748', fontSize: '16px', borderBottom: '1px solid #edf2f7', paddingBottom: '10px' }}>📈 Platform Growth Velocity</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <tbody>
                            {[{ label: 'Last 24 Hours', val: growth.day }, { label: 'Last 7 Days', val: growth.week }, { label: 'Last 30 Days', val: growth.month }, { label: 'Year to Date', val: growth.yearToDate }].map((row, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f7fafc' }}>
                                    <td style={{ padding: '12px 0', color: '#718096', fontWeight: '500' }}>{row.label}</td>
                                    <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: 'bold', color: row.val > 0 ? '#38a169' : '#cbd5e0' }}>{row.val > 0 ? `+${row.val} Servers` : '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                    <h3 style={{ margin: '0 0 20px 0', color: '#2d3748', fontSize: '16px', borderBottom: '1px solid #edf2f7', paddingBottom: '10px' }}>⚠️ Action Required / Watch Out</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '10px' }}>
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '5px', fontWeight: 'bold', color: '#dd6b20' }}><span>Nearing Expiry (&lt; 30 Days)</span><span>{serverRisk.nearingExpiry}</span></div>
                            <div style={{ width: '100%', height: '8px', background: '#edf2f7', borderRadius: '4px' }}><div style={{ width: `${metrics.totalServers ? (serverRisk.nearingExpiry / metrics.totalServers) * 100 : 0}%`, height: '100%', background: '#dd6b20' }}></div></div>
                        </div>
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '5px', fontWeight: 'bold', color: '#e53e3e' }}><span>Near Capacity Limit (≥ 90%)</span><span>{serverRisk.highCapacity}</span></div>
                            <div style={{ width: '100%', height: '8px', background: '#edf2f7', borderRadius: '4px' }}><div style={{ width: `${metrics.totalServers ? (serverRisk.highCapacity / metrics.totalServers) * 100 : 0}%`, height: '100%', background: '#e53e3e' }}></div></div>
                        </div>
                    </div>
                </div>

                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <h3 style={{ margin: '0 0 5px 0', color: '#2d3748', fontSize: '16px', borderBottom: '1px solid #edf2f7', paddingBottom: '10px' }}>⚡ Quick Actions</h3>
                    
                    {canCreateServer && (
                        <button onClick={() => navigate('/servers')} style={{ padding: '12px', backgroundColor: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', textAlign: 'left' }}>+ Add New Server</button>
                    )}
                    
                    <button onClick={() => navigate('/global-chat')} style={{ padding: '12px', backgroundColor: '#f0fff4', color: '#2f855a', border: '1px solid #c6f6d5', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', textAlign: 'left' }}>💬 Open Global Chat</button>
                    <button onClick={() => navigate('/users')} style={{ padding: '12px', backgroundColor: '#faf5ff', color: '#6b46c1', border: '1px solid #e9d8fd', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', textAlign: 'left' }}>👥 Search Global Directory</button>
                </div>
            </div>
        </div>
    );
}